import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import "../form.css";

const GameConfigList = () => {
  const [gameConfigs, setGameConfigs] = useState([]);
  const [loadingId, setLoadingId] = useState(null); // Track the loading state for a specific button
  const [showPopup, setShowPopup] = useState(false); // State for showing the popup
  const [duplicateData, setDuplicateData] = useState({ oldId: '', newId: '', newName: '' }); // Data for duplication
  const [formatPopupId, setFormatPopupId] = useState(null); // Config id en attente de choix de format (A4/A5)
  const navigate = useNavigate();

  // Fetch les configurations via l'API
  useEffect(() => {
    const fetchGameConfigs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/game_config_list_complete`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        // Sort configurations alphabetically by name
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setGameConfigs(sortedData);
      } catch (error) {
        console.error('Erreur lors du chargement des configurations :', error);
      }
    };

    fetchGameConfigs();
  }, []);

  const handleDownload = async (gameConfigId, format = 'a4') => {
    setFormatPopupId(null); // Ferme le popup de choix de format
    setLoadingId(gameConfigId); // Set loading state for the current button
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_pdf/${gameConfigId}?format=${format}`);

      if (response.ok) {
        // La réponse est OK, alors traitons la réponse comme nécessaire
        // Dans cet exemple, nous allons simplement rediriger l'utilisateur vers le rapport téléchargé
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `game_notebook_${gameConfigId}_${format}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

      } else {
        console.error('Erreur lors du téléchargement du rapport.');

      }
    } catch (error) {
      console.error('Erreur lors du téléchargement du rapport :', error);

    } finally {
      setLoadingId(null); // Reset loading state
    }
  };

  // Fonction pour rediriger vers l'éditeur
  const handleEdit = (configId) => {
    navigate(`/game_config_editor/${configId}`);
  };

  // Fonction pour rediriger vers le créateur de config
  const handleCreateNew = () => {
    navigate("/game_config_creator");
  };

  const handleDuplicate = (oldId) => {
    const oldConfig = gameConfigs.find(config => config._id === oldId);
    setDuplicateData({
      oldId,
      newId: oldConfig._id, // Pre-fill with the old ID
      newName: oldConfig.name // Pre-fill with the old name
    });
    setShowPopup(true);
  };

  const handleDuplicateSubmit = async () => {

    const idExists = gameConfigs.some(config => config._id === duplicateData.newId);

    if (idExists) {
      toast.error("Cet ID est déjà utilisé. Veuillez en choisir un autre.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/duplicate_game_config`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          old_id: duplicateData.oldId,
          new_id: duplicateData.newId,
          new_name: duplicateData.newName,
        }),
      });

      if (response.ok) {
        setShowPopup(false);
        window.location.reload(); // Reload the page after duplication
      } else {
        toast.error("Erreur lors de la duplication de la configuration.");
      }
    } catch (error) {
      console.error('Erreur lors de la duplication de la configuration :', error);
      toast.error("Erreur réseau lors de la duplication.");
    }
  };

  return (
    <div className="game-config-list-container">
      <div className="game-config-list-header">
        <button 
          onClick={handleCreateNew} 
          className="game-config-list-blue-button"
          disabled={loadingId !== null} // Disable during loading
        >
          Créer une nouvelle configuration
        </button>
      </div>
      <ul className="game-config-list">
        {gameConfigs.map((config) => (
          <li key={config._id} className="game-config-list-item">
            <span className="game-config-list-name">{config.name}</span>
            <div className='game-config-list-button-container'>
                <button
                onClick={() => setFormatPopupId(config._id)}
                className="game-config-list-orange-button"
                style={{
                  marginRight: '5px',
                  backgroundColor: loadingId === config._id ? '#faa08a' : '', // Lighter orange during loading
                  cursor: loadingId ? 'not-allowed' : 'pointer', // Change cursor during loading
                  padding: loadingId === config._id ? '0 38px' : '' // Add padding during loading
                }}
                disabled={loadingId !== null} // Disable all buttons during loading
                >
                {loadingId === config._id ? 'Chargement...' : 'Télécharger carnet'}
                </button>
                <button 
                onClick={() => handleEdit(config._id)} 
                className="game-config-list-orange-button"
                disabled={loadingId !== null} // Disable all buttons during loading
                style={{marginRight: '5px'}}
                >
                Éditer
                </button>
                <button 
                onClick={() => handleDuplicate(config._id)} 
                className="game-config-list-orange-button"
                disabled={loadingId !== null} // Disable all buttons during loading
                >
                Dupliquer
                </button>
            </div>
          </li>
        ))}
      </ul>

      {formatPopupId && (
        <div className="popup">
          <div className="popup-content">
            <h3>Format du carnet</h3>
            <p>Choisissez le format des carnets d'acteurs à télécharger.</p>
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '-6px' }}>
              A4 : carnets d'acteurs en livret imposé.<br />
              A5 : carnets d'acteurs en pages dans l'ordre de lecture.<br />
              Les autres documents (agence de l'eau, chevalets, plateau, fiche) restent en A4 dans les deux cas.
            </p>
            <button onClick={() => setFormatPopupId(null)} className="blue-button popup-cancel-button">Annuler</button>
            <button onClick={() => handleDownload(formatPopupId, 'a5')} className="orange-button popup-submit-button" style={{ marginRight: '5px' }}>A5</button>
            <button onClick={() => handleDownload(formatPopupId, 'a4')} className="orange-button popup-submit-button">A4</button>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup">
          <div className="popup-content">
            <h3>Dupliquer la configuration</h3>
            <label>
              Nouvel ID :
              <input 
                type="text" 
                value={duplicateData.newId} 
                onChange={(e) => setDuplicateData({ ...duplicateData, newId: e.target.value })} 
              />
            </label>
            <label>
              Nouveau nom :
              <input 
                type="text" 
                value={duplicateData.newName} 
                onChange={(e) => setDuplicateData({ ...duplicateData, newName: e.target.value })} 
              />
            </label>
            <button onClick={() => setShowPopup(false)} className="blue-button popup-cancel-button">Annuler</button>
            <button onClick={handleDuplicateSubmit} className="orange-button popup-submit-button">Valider</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameConfigList;
